import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CalendarCheck,
  ChevronDown,
  CircleCheck,
  Clock3,
  ServerCog,
  Smartphone,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import { fmtDateTime, fmtTimeAgo } from '@siva/shared';
import { api, post } from '../lib/api.js';
import { EmptyState } from '../lib/components.js';
import { useOverlay } from '../lib/overlay.js';
import { t } from '../i18n/index.js';

type Item = {
  id: string;
  kind: 'flag' | 'job' | 'rereg' | 'early_out' | 'swap' | 'attention';
  tone: 'red' | 'amber' | 'violet' | 'teal' | 'neutral';
  title: string;
  detail: string;
  at: string;
  url: string;
  /** Set when this row stands for several of the same thing on the same day. */
  count?: number;
  children?: Item[];
  /** A standing problem: shown first, and never part of the unread count. */
  pinned?: boolean;
};

const ICON: Record<Item['kind'], LucideIcon> = {
  flag: TriangleAlert,
  job: CircleCheck,
  rereg: Smartphone,
  early_out: Clock3,
  swap: CalendarCheck,
  attention: ServerCog,
};

/**
 * The server decides what kinds exist, and it gained one before this map did — which rendered an
 * undefined component and took the whole panel down with it. A missing icon is not worth losing
 * the notification over.
 */
const iconFor = (kind: Item['kind']): LucideIcon => ICON[kind] ?? Bell;

/**
 * The bell in the top bar: unread count, and a dropdown (a full sheet on phones) with the last
 * events. Opening it loads the feed; "Mark all read" stamps this admin's last-seen time.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useOverlay(open);
  const [items, setItems] = useState<Item[] | null>(null);
  const [unread, setUnread] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);

  const poll = useCallback(async () => {
    try {
      const r = await api<{ unread: number }>('/admin/feed/count');
      setUnread(r.unread);
    } catch {
      /* the badge is not worth an error banner */
    }
  }, []);

  useEffect(() => {
    void poll();
    const id = setInterval(() => void poll(), 60_000);
    return () => clearInterval(id);
  }, [poll]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    void api<{ items: Item[]; unread: number }>('/admin/feed?limit=30')
      .then((r) => {
        if (!alive) return;
        setItems(r.items);
        setUnread(r.unread);
      })
      .catch(() => alive && setItems([]));
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onClick = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      alive = false;
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const markRead = async () => {
    try {
      await post('/admin/feed/read');
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  const go = (item: Item) => {
    setOpen(false);
    const [path, hash] = item.url.split('#');
    navigate({ pathname: path || '/', ...(hash ? { hash: `#${hash}` } : {}) });
  };

  return (
    <div className="bell-wrap" ref={wrap}>
      <button
        type="button"
        className="icon-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={unread ? t('feed_title_unread', { n: unread }) : t('feed_title')}
        title={t('feed_title')}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={18} />
        {unread > 0 && <span className="count-badge">{unread}</span>}
      </button>
      {open && (
        <div className="feed-panel" role="dialog" aria-label={t('feed_title')}>
          <div className="feed-head">
            <strong>{t('feed_title')}</strong>
            <span className="row">
              <button type="button" className="link" onClick={() => void markRead()}>
                {t('feed_mark_read')}
              </button>
              <button
                type="button"
                className="icon-btn small feed-close"
                aria-label={t('close')}
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </span>
          </div>
          <div className="feed-list">
            {items === null ? (
              <p className="muted feed-loading">{t('loading')}</p>
            ) : items.length === 0 ? (
              <EmptyState icon={CircleCheck} text={t('feed_empty')} />
            ) : (
              <ul className="list">
                {items.map((i) => (
                  <FeedRow key={i.id} item={i} onGo={go} />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One row. Repeats of the same thing on the same day arrive as a single row carrying its
 * children: "3 punches outside the geofence today" opens to show which three, so the panel says
 * how much happened without spending three rows saying it.
 */
function FeedRow({ item, onGo }: { item: Item; onGo: (i: Item) => void }) {
  const [open, setOpen] = useState(false);
  const Icon = iconFor(item.kind);
  const grouped = !!item.count && !!item.children?.length;

  const body = (
    <>
      <span className={`feed-icon tone-${item.tone}`} aria-hidden="true">
        <Icon size={14} />
      </span>
      <span className="feed-body">
        <span className="feed-title">{item.title}</span>
        {item.detail && <span className="feed-detail">{item.detail}</span>}
      </span>
      <span className="feed-time" title={fmtDateTime(item.at)}>
        {item.pinned ? t('feed_now') : fmtTimeAgo(item.at)}
        {grouped && <ChevronDown size={14} className="feed-chevron" aria-hidden="true" />}
      </span>
    </>
  );

  if (!grouped) {
    return (
      <li>
        <button type="button" className="feed-item" onClick={() => onGo(item)}>
          {body}
        </button>
      </li>
    );
  }
  return (
    <li className={open ? 'feed-group open' : 'feed-group'}>
      <button
        type="button"
        className="feed-item"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {body}
      </button>
      {open && (
        <ul className="list feed-children">
          {item.children!.map((c) => (
            <FeedRow key={c.id} item={c} onGo={onGo} />
          ))}
        </ul>
      )}
    </li>
  );
}
